/**
 * KioskPrepressDept - Prepress Department Kiosk View
 * 
 * Features:
 * - No login required (uses kiosk access key in URL)
 * - Auto-refreshes every 60 seconds
 * - Left column: Pending issues (Works Docket Not Issued, Waiting on Artwork, On Proof with Client, Problem with PDF)
 * - Middle section: 4 Prepress columns (Prepress, Prepress_Digital, Prepress_Litho, Prepress_Wide Format)
 * - Right column: Gone to Press (Digital Print, Litho Print, Wide Format, combos)
 * - Shows timestamp of when job was last updated
 * 
 * Usage: /kiosk/prepress-dept?key=YOUR_KIOSK_KEY
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { 
  RefreshCw, Maximize2, Minimize2, AlertTriangle, Printer,
  Wifi, WifiOff, Package, Clock, FileText, CheckCircle, AlertCircle,
  Play, History
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status group definitions
const PENDING_STATUSES = [
  "Works Docket Not Issued",
  "Waiting on Artwork",
  "Problem with PDF | Job Info"
];

const ON_PROOF_STATUS = "On Proof with Client";

const PREPRESS_COLUMNS = [
  { id: 'prepress', title: 'PREPRESS', status: 'Prepress', color: 'bg-blue-600' },
  { id: 'digital', title: 'PREPRESS DIGITAL', status: 'Prepress_Digital', color: 'bg-purple-600' },
  { id: 'litho', title: 'PREPRESS LITHO', status: 'Prepress_Litho', color: 'bg-indigo-600' },
  { id: 'wide_format', title: 'PREPRESS WIDE FORMAT', status: 'Prepress_Wide Format', color: 'bg-cyan-600' }
];

const GONE_TO_PRESS_STATUSES = [
  "Digital Print",
  "Litho Print", 
  "Wide Format",
  "Digital & Wide Format",
  "Litho & Wide Format",
  "Digital & Litho",
  "WideFormat~Digital&Litho"
];

// Status badge colors
const STATUS_COLORS = {
  'Works Docket Not Issued': 'bg-red-100 text-red-700',
  'Waiting on Artwork': 'bg-amber-100 text-amber-700',
  'On Proof with Client': 'bg-blue-100 text-blue-700',
  'Problem with PDF | Job Info': 'bg-red-100 text-red-700',
  'Prepress': 'bg-blue-100 text-blue-700',
  'Prepress_Digital': 'bg-purple-100 text-purple-700',
  'Prepress_Litho': 'bg-indigo-100 text-indigo-700',
  'Prepress_Wide Format': 'bg-cyan-100 text-cyan-700',
  'Digital Print': 'bg-green-100 text-green-700',
  'Litho Print': 'bg-green-100 text-green-700',
  'Wide Format': 'bg-green-100 text-green-700',
};

const KioskPrepressDept = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isConnected, setIsConnected] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  const [activeJobIds, setActiveJobIds] = useState(new Set());

  // Get kiosk key from URL
  const urlParams = new URLSearchParams(window.location.search);
  const kioskKey = urlParams.get('key') || '';

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true));
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false));
    }
  };

  // Auto-enter fullscreen on load
  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    setTimeout(() => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(() => {});
      }
    }, 1000);
    
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Fetch jobs
  const fetchJobs = useCallback(async () => {
    if (!kioskKey) {
      setError('Missing kiosk access key. Add ?key=YOUR_KEY to URL');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/kiosk/prepress-dept?key=${kioskKey}&limit=300`);
      setJobs(response.data.jobs || []);
      setIsConnected(true);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
      setIsConnected(false);
      if (err.response?.status === 401) {
        setError('Invalid kiosk access key');
      }
    } finally {
      setLoading(false);
    }
  }, [kioskKey]);

  // Fetch active tracking
  const fetchActiveTracking = useCallback(async () => {
    if (!kioskKey) return;
    
    try {
      const response = await axios.get(`${API}/kiosk/active-tracking?key=${kioskKey}`);
      const activeIds = new Set((response.data.active_jobs || []).map(j => j.job_id));
      setActiveJobIds(activeIds);
    } catch (err) {
      console.error('Failed to fetch active tracking:', err);
    }
  }, [kioskKey]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    fetchJobs();
    fetchActiveTracking();
    const jobsInterval = setInterval(fetchJobs, 60000);
    const trackingInterval = setInterval(fetchActiveTracking, 5000);
    return () => {
      clearInterval(jobsInterval);
      clearInterval(trackingInterval);
    };
  }, [fetchJobs, fetchActiveTracking]);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Check if job is late
  const isJobLate = (job) => {
    if (!job.date_due || job.date_due === '0000-00-00') return false;
    const dueDate = new Date(job.date_due);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  // Get jobs for specific statuses, sorted by due date
  const getJobsForStatuses = (statuses) => {
    return jobs
      .filter(job => statuses.includes(job.printlogic_status))
      .sort((a, b) => {
        const dateA = a.date_due && a.date_due !== '0000-00-00' ? new Date(a.date_due) : new Date('9999-12-31');
        const dateB = b.date_due && b.date_due !== '0000-00-00' ? new Date(b.date_due) : new Date('9999-12-31');
        return dateA - dateB;
      });
  };

  // Get jobs for single status
  const getJobsForStatus = (status) => {
    return jobs
      .filter(job => job.printlogic_status === status)
      .sort((a, b) => {
        const dateA = a.date_due && a.date_due !== '0000-00-00' ? new Date(a.date_due) : new Date('9999-12-31');
        const dateB = b.date_due && b.date_due !== '0000-00-00' ? new Date(b.date_due) : new Date('9999-12-31');
        return dateA - dateB;
      });
  };

  // Get jobs that went to press within the last hour
  const getGoneToPressJobs = () => {
    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000); // 1 hour ago
    
    return jobs
      .filter(job => {
        if (!GONE_TO_PRESS_STATUSES.includes(job.printlogic_status)) return false;
        
        // Check if job was tagged within the last hour
        const timestamp = job.last_synced_at || job.status_changed_at || job.updated_at;
        if (!timestamp) return false;
        
        const taggedTime = new Date(timestamp);
        return taggedTime >= oneHourAgo;
      })
      .sort((a, b) => {
        // Sort by most recently tagged first
        const timeA = new Date(a.last_synced_at || a.status_changed_at || a.updated_at || 0);
        const timeB = new Date(b.last_synced_at || b.status_changed_at || b.updated_at || 0);
        return timeB - timeA;
      });
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    return date.toLocaleString('en-IE', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Compact Job Card Component
  const JobCard = ({ job, showStatus = false }) => {
    const late = isJobLate(job);
    const isActive = activeJobIds.has(job.job_id);
    const timestamp = job.last_synced_at || job.status_changed_at || job.updated_at;

    return (
      <div
        className={`
          p-2 rounded-lg border-2 transition-all text-xs h-fit
          ${isActive 
            ? 'bg-green-50 border-green-400 shadow-md ring-2 ring-green-300' 
            : late 
              ? 'bg-red-50 border-red-300' 
              : 'bg-white border-gray-200 shadow-sm'
          }
        `}
      >
        {/* Header Row */}
        <div className="flex items-center justify-between gap-1 mb-1">
          <div className="flex items-center gap-1 min-w-0">
            <span className={`text-sm font-black ${isActive ? 'text-green-700' : late ? 'text-red-700' : 'text-blue-700'}`}>
              #{job.job_id}
            </span>
            {isActive && <Play className="w-3 h-3 text-green-600 animate-pulse" />}
            {late && !isActive && <AlertTriangle className="w-3 h-3 text-red-500" />}
          </div>
          {/* Due Date */}
          {job.date_due && job.date_due !== '0000-00-00' && (
            <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold ${
              late ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
            }`}>
              <Clock className="w-3 h-3" />
              <span>{new Date(job.date_due).toLocaleDateString('en-IE', { day: 'numeric', month: 'short' })}</span>
            </div>
          )}
        </div>

        {/* Customer */}
        <p className="font-semibold text-gray-800 truncate text-xs">{job.customer}</p>
        
        {/* Product */}
        <p className="text-gray-500 truncate text-[10px] mb-1">{job.product}</p>

        {/* Status badge (if showing) */}
        {showStatus && (
          <div className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold mb-1 ${STATUS_COLORS[job.printlogic_status] || 'bg-gray-100 text-gray-600'}`}>
            {job.printlogic_status}
          </div>
        )}

        {/* Timestamp - when last tagged */}
        {timestamp && (
          <div className="flex items-center gap-0.5 text-[10px] text-gray-400 mb-1">
            <History className="w-3 h-3" />
            <span>Tagged: {formatTimestamp(timestamp)}</span>
          </div>
        )}

        {/* Footer Row */}
        <div className="flex items-center justify-between text-[10px] text-gray-500 pt-1 border-t border-gray-100">
          <div className="flex items-center gap-0.5">
            <Package className="w-3 h-3" />
            <span className="font-bold text-gray-700">Qty: {job.quantity?.toLocaleString() || 1}</span>
          </div>
        </div>
      </div>
    );
  };

  // Mini Job Card for Pending column - ultra compact to fit without scroll
  const MiniJobCard = ({ job }) => {
    const late = isJobLate(job);
    const isActive = activeJobIds.has(job.job_id);

    return (
      <div
        className={`
          px-2 py-1.5 rounded border transition-all text-[10px]
          ${isActive 
            ? 'bg-green-50 border-green-400' 
            : late 
              ? 'bg-red-50 border-red-300' 
              : 'bg-gray-50 border-gray-200'
          }
        `}
      >
        <div className="flex items-center justify-between gap-1">
          <span className={`font-black ${isActive ? 'text-green-700' : late ? 'text-red-700' : 'text-blue-700'}`}>
            #{job.job_id}
          </span>
          {job.date_due && job.date_due !== '0000-00-00' && (
            <span className={`text-[9px] ${late ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
              {new Date(job.date_due).toLocaleDateString('en-IE', { day: 'numeric', month: 'short' })}
            </span>
          )}
        </div>
        <p className="text-gray-700 truncate font-medium">{job.customer}</p>
        <div className={`inline-block px-1 py-0.5 rounded text-[9px] font-bold mt-0.5 ${STATUS_COLORS[job.printlogic_status] || 'bg-gray-100 text-gray-600'}`}>
          {job.printlogic_status?.replace('Problem with PDF | Job Info', 'PDF Problem')}
        </div>
      </div>
    );
  };

  // Error screen
  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center p-8">
          <AlertTriangle className="w-20 h-20 text-red-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Kiosk Error</h1>
          <p className="text-xl text-red-600 mb-6">{error}</p>
          <p className="text-gray-500">
            URL format: /kiosk/prepress-dept?key=YOUR_KIOSK_KEY
          </p>
        </div>
      </div>
    );
  }

  // Loading screen
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-2xl font-bold text-gray-800">Loading Prepress Department...</p>
          <p className="text-gray-500 mt-2">Kiosk Mode</p>
        </div>
      </div>
    );
  }

  const pendingJobs = getJobsForStatuses(PENDING_STATUSES);
  const onProofJobs = getJobsForStatus(ON_PROOF_STATUS);
  const goneToPress = getGoneToPressJobs();

  return (
    <div className="h-screen bg-gray-100 flex flex-col overflow-hidden">
      {/* Compact Header */}
      <header className="bg-white border-b-2 border-gray-200 shadow-sm px-4 py-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          {/* Logo and Title */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center shadow">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-black text-gray-800 tracking-tight">
                PREPRESS DEPARTMENT
              </h1>
              <p className="text-xs text-gray-500">
                {jobs.length} active jobs
              </p>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-4">
            {/* Connection Status */}
            <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-sm ${
              isConnected ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {isConnected ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
              <span className="font-medium">{isConnected ? 'Online' : 'Offline'}</span>
            </div>

            {/* Last Update */}
            {lastUpdate && (
              <div className="text-xs text-gray-500">
                {lastUpdate.toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' })}
              </div>
            )}

            {/* Current Time */}
            <div className="text-2xl font-mono font-bold text-gray-800">
              {currentTime.toLocaleTimeString('en-IE', { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: false 
              })}
            </div>

            {/* Fullscreen Toggle */}
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
            >
              {isFullscreen ? (
                <Minimize2 className="w-5 h-5 text-gray-700" />
              ) : (
                <Maximize2 className="w-5 h-5 text-gray-700" />
              )}
            </button>

            {/* Refresh Button */}
            <button
              onClick={fetchJobs}
              className="p-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              <RefreshCw className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 p-2 overflow-hidden">
        <div className="flex gap-2 h-full">
          
          {/* Left Column - Pending/Issues (narrow, no scroll - stacked compact) */}
          <div className="w-48 flex-shrink-0 flex flex-col bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
            <div className="bg-amber-500 px-3 py-2 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <AlertCircle className="w-4 h-4 text-white" />
                  <h2 className="text-sm font-black text-white uppercase">PENDING</h2>
                </div>
                <span className="bg-white/20 text-white text-sm font-bold px-2 py-0.5 rounded-full">
                  {pendingJobs.length}
                </span>
              </div>
            </div>
            <div className="flex-1 p-1.5 overflow-hidden">
              {pendingJobs.length === 0 ? (
                <div className="h-full flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <CheckCircle className="w-8 h-8 mx-auto mb-1 opacity-30" />
                    <p className="text-xs">All clear</p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  {pendingJobs.map((job) => (
                    <MiniJobCard key={job.job_id} job={job} />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* On Proof with Client Column */}
          <div className="w-48 flex-shrink-0 flex flex-col bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
            <div className="bg-blue-500 px-3 py-2 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xs font-black text-white uppercase">ON PROOF</h2>
                  <p className="text-[10px] text-white/80">with Client</p>
                </div>
                <span className="bg-white/20 text-white text-sm font-bold px-2 py-0.5 rounded-full">
                  {onProofJobs.length}
                </span>
              </div>
            </div>
            <div className="flex-1 p-1.5 overflow-hidden">
              {onProofJobs.length === 0 ? (
                <div className="h-full flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <CheckCircle className="w-8 h-8 mx-auto mb-1 opacity-30" />
                    <p className="text-xs">None</p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  {onProofJobs.map((job) => (
                    <MiniJobCard key={job.job_id} job={job} />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Middle Section - 4 Prepress Columns */}
          <div className="flex-1 flex gap-2 overflow-hidden">
            {PREPRESS_COLUMNS.map((column) => {
              const columnJobs = getJobsForStatus(column.status);
              return (
                <div key={column.id} className="flex-1 flex flex-col bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden min-w-0">
                  <div className={`${column.color} px-3 py-2 flex-shrink-0`}>
                    <div className="flex items-center justify-between">
                      <h2 className="text-sm font-black text-white uppercase truncate">{column.title}</h2>
                      <span className="bg-white/20 text-white text-sm font-bold px-2 py-0.5 rounded-full">
                        {columnJobs.length}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 p-2 overflow-y-auto">
                    {columnJobs.length === 0 ? (
                      <div className="h-full flex items-center justify-center text-gray-400">
                        <div className="text-center">
                          <Package className="w-8 h-8 mx-auto mb-1 opacity-30" />
                          <p className="text-xs">No jobs</p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2">
                        {columnJobs.map((job) => (
                          <JobCard key={job.job_id} job={job} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right Column - Gone to Press within 1 hour (narrow, scrollable) */}
          <div className="w-56 flex-shrink-0 flex flex-col bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
            <div className="bg-green-600 px-3 py-2 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-1">
                    <Printer className="w-4 h-4 text-white" />
                    <h2 className="text-xs font-black text-white uppercase">GONE TO PRESS</h2>
                  </div>
                  <p className="text-[10px] text-white/80">within last 1HR</p>
                </div>
                <span className="bg-white/20 text-white text-sm font-bold px-2 py-0.5 rounded-full">
                  {goneToPress.length}
                </span>
              </div>
            </div>
            <div className="flex-1 p-2 overflow-y-auto">
              {goneToPress.length === 0 ? (
                <div className="h-full flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <Package className="w-8 h-8 mx-auto mb-1 opacity-30" />
                    <p className="text-xs">No jobs</p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {goneToPress.map((job) => (
                    <JobCard key={job.job_id} job={job} showStatus={true} />
                  ))}
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default KioskPrepressDept;
